/**
 * Retrouver le COMPTE Discord d'un candidat — pour lui écrire en un clic.
 *
 * Kayla, 19-08-2026 : « j'aimerais bien avoir un bouton où je clique pour
 * avoir leur Discord direct, pour aller les DM facilement. »
 *
 * 🩸 Le pseudo saisi au formulaire ne suffit pas, et c'est le cœur du sujet :
 * un DM s'ouvre par l'ID du compte (`discord.com/users/<id>`), il n'existe
 * AUCUNE URL par pseudo. Il faut donc rapprocher ce que le candidat a tapé de
 * ce que Discord connaît. Deux sources, dans cet ordre :
 *
 *   1. `cockpit` — le membre a connecté son Discord au cockpit (OAuth). L'ID
 *      est VÉRIFIÉ : c'est Discord lui-même qui l'a donné. Rapproché par
 *      e-mail, jamais par pseudo.
 *   2. `serveur` — le pseudo saisi est, au caractère près, celui d'un membre
 *      du serveur. Bonne dans l'immense majorité des cas (mesuré le 19-08 :
 *      `krishna097567`, `fetra812`, `tonnyignace3627`, `m.lucien`… saisis
 *      exactement), et c'est la seule qui couvre un candidat qui a rejoint le
 *      Discord sans jamais créer son compte cockpit — c'est-à-dire presque
 *      tout le monde.
 *
 * ⛔ AUCUN rapprochement APPROCHANT. Ni préfixe, ni distance d'édition, ni
 * « ça y ressemble ». Mesuré le même jour : un candidat a saisi `raoods_523`
 * pour un compte qui s'appelle `toods0227` — un algorithme indulgent les
 * aurait mariés, et Kayla aurait écrit à un inconnu en croyant relancer son
 * candidat. Un bouton absent se voit ; un bouton qui ment, non.
 */
import type { CandidateListItem } from "./types";

/** Un membre du serveur Discord, tel que le bot le voit. */
export interface MembreDuServeur {
  id: string;
  username: string;
}

/** Un membre du cockpit, tel que Convex le connaît. */
export interface MembreDuCockpit {
  email: string | null;
  discordId: string | null;
}

export type SourceDuDiscord = "cockpit" | "serveur";

export interface DiscordTrouve {
  /** L'ID du compte Discord — ce qui ouvre le DM. */
  id: string;
  source: SourceDuDiscord;
}

/** Adresse comparable : les candidats tapent `Toods42@outlook.com`, WorkOS
 *  stocke en minuscules. */
function cleEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Pseudo comparable.
 *
 * Minuscules, espaces retirés, `@` de tête ignoré, et l'ancien discriminant
 * `#1234` coupé — quatre écritures d'un même pseudo qu'on a vraiment vues
 * passer (`MialyNanouh _76439` porte une espace au milieu).
 *
 * 🪤 On retire les espaces INTERNES : un pseudo Discord ne peut pas en
 * contenir, donc celui qui en tape une s'est trompé de touche, pas de compte.
 *
 * ⚠️ Exportée depuis le 20-08-2026 : la recherche de l'onglet Équipe compare
 * elle aussi un pseudo tapé à un pseudo stocké, et une SECONDE normalisation
 * écrite à côté finirait par diverger de celle-ci. Une seule règle, deux
 * appelants.
 */
export function clePseudo(pseudo: string): string {
  return pseudo
    .trim()
    .toLowerCase()
    .replace(/^@/, "")
    .replace(/#\d{4}$/, "")
    .replace(/\s+/g, "");
}

/**
 * Pour chaque lead, le compte Discord qu'on sait ouvrir — ou rien.
 *
 * La clé de la map est l'`id` du lead : deux candidatures d'une même personne
 * (ça arrive, deux dépôts en sept minutes le 19-08) restent deux lignes.
 */
export function discordParLead(
  leads: CandidateListItem[],
  membresDuCockpit: MembreDuCockpit[],
  membresDuServeur: MembreDuServeur[],
): Map<string, DiscordTrouve> {
  const parEmail = new Map<string, string>();
  for (const m of membresDuCockpit) {
    if (!m.email || !m.discordId) continue;
    parEmail.set(cleEmail(m.email), m.discordId);
  }

  // 🪤 Deux comptes du serveur qui normalisent pareil (ça ne devrait pas
  // arriver — Discord impose l'unicité — mais la normalisation, elle, peut
  // les rapprocher) : on n'en garde AUCUN. Choisir au hasard entre deux
  // personnes est la faute que ce fichier refuse.
  const vus = new Map<string, string | null>();
  for (const m of membresDuServeur) {
    const k = clePseudo(m.username);
    if (!k) continue;
    vus.set(k, vus.has(k) ? null : m.id);
  }

  const trouves = new Map<string, DiscordTrouve>();
  for (const lead of leads) {
    if (lead.email) {
      const parCockpit = parEmail.get(cleEmail(lead.email));
      if (parCockpit) {
        trouves.set(lead.id, { id: parCockpit, source: "cockpit" });
        continue;
      }
    }
    const pseudo = lead.contact_discord?.trim();
    if (!pseudo) continue;
    const parServeur = vus.get(clePseudo(pseudo));
    if (parServeur) trouves.set(lead.id, { id: parServeur, source: "serveur" });
  }
  return trouves;
}

/** L'adresse qui ouvre la conversation privée. Web et application desktop
 *  ouvrent toutes deux le profil, d'où le bouton « Message ». */
export function lienDuDm(discordId: string): string {
  return `https://discord.com/users/${discordId}`;
}
